import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Header from '@components/header'
import { columnSalesOrderAdmin } from '@utils/columns'
import { getToday } from '@utils/time'
import Layout from '@components/Layout'
import { useSelector } from 'react-redux'
import ViewSalesModal from '@components/Modal/ViewSalesModal/index'
import { useQuery } from '@tanstack/react-query'
import { fetchSalesRequestAdmin } from '@utils/fetchData'
import { debouncedSet } from '@utils/debounce'
import SuccessModal from '@components/Modal/SuccessModal'
import ErrorModal from '@components/Modal/ErrorModal'
import ConfirmDeleteModal from '@components/Modal/ConfirmDeleteModal'
import { http } from '@services/http'
import IgnoreSaleModal from '@components/Modal/ignoreSaleModal'
import { changePageSize, changePagination } from '@utils/pagination'

const SalesOrderRequestAdmin = () => {
	const { t } = useTranslation()
	const successRef = useRef()
	const errorRef = useRef()

	const { roleNames } = useSelector((state) => state.main)
	const formattedDate = getToday()

	const [currentPage, setCurrentPage] = useState(0)
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [isBtnLoading, setIsBtnLoading] = useState(false)
	const [isCancelModalVisible, setIsCancelModalVisible] = useState(false)
	const [selectedRowData, setSelectedRowData] = useState(null)
	const [docNum, setDocNum] = useState('')
	const [cardName, setCardName] = useState('')
	const [slpName, setSlpName] = useState('')
	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState(formattedDate)
	const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false)
	const [pageSize, setPageSize] = useState(10)

	const {
		data = [],
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [
			'salesOrder',
			currentPage,
			cardName,
			docNum,
			startDate,
			endDate,
			slpName,
			'Yes',
			pageSize,
		],
		queryFn: fetchSalesRequestAdmin,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const cancelSale = async (record) => {
		setIsConfirmModalVisible(false)
		setIsBtnLoading(true)
		try {
			await http.post(`api/sales/cancel-sales-order/${record?.docEntry}`)
			successRef.current.open(t('sucDeleted'))
			refetch()
		} catch (error) {
			errorRef.current.open(error?.response?.data?.message)
		} finally {
			setIsBtnLoading(false)
		}
	}

	const handleNextPage = () => {
		if (data.length === 10) {
			setCurrentPage((prevPage) => prevPage + 1)
		}
	}

	const handlePreviousPage = () => {
		if (currentPage > 0) {
			setCurrentPage((prevPage) => prevPage - 1)
		}
	}

	const handleRowClick = (record) => {
		setSelectedRowData(record)
		setIsModalVisible(true)
	}

	const handleIgnoreClick = (record) => {
		setSelectedRowData(record)
		setIsCancelModalVisible(true)
	}

	const handleStartDate = (e) => {
		setStartDate(e.target.value)
	}

	const handleEndDate = (e) => {
		setEndDate(e.target.value)
	}

	const handleDocNumChange = (val) => {
		debouncedSet(val, setDocNum)
	}

	const handleCardNameChange = (val) => {
		debouncedSet(val, setCardName)
	}

	const handleSlpNameChange = (val) => {
		debouncedSet(val, setSlpName)
	}

	const handleAcceptCancelClick = (record) => {
		setSelectedRowData(record)
		setIsConfirmModalVisible(true)
	}

	const columns = columnSalesOrderAdmin(
		t,
		handleRowClick,
		handleDocNumChange,
		handleCardNameChange,
		handleSlpNameChange,
		handleIgnoreClick,
		handleAcceptCancelClick,
		isBtnLoading,
		t('acceptCancel'),
		t('ignore'),
	)

	const handlePaginationChange = (page, pageSize) => {
		changePagination({
			page,
			pageSize,
			setCurrentPage,
			setPageSize,
		})
	}
	const handlePageSizeChange = (current, pageSize) => {
		changePageSize({
			current,
			pageSize,
			setCurrentPage,
			setPageSize,
		})
	}

	return (
		<Layout>
			<Header
				title={t('salesOrderRequest')}
				columns={columns}
				fdata={data}
				loading={isLoading}
				onStartDateChange={handleStartDate}
				onEndDateChange={handleEndDate}
				startDate={startDate}
				endDate={endDate}
				isSticky={false}
				formattedDate={formattedDate}
				isShowPagination
				handlePageSizeChange={handlePageSizeChange}
				handlePaginationChange={handlePaginationChange}
			/>
			<ViewSalesModal
				visible={isModalVisible}
				data={selectedRowData}
				onClose={() => setIsModalVisible(false)}
				ship={true}
				incoming={true}
				refetch={refetch}
			/>

			<ConfirmDeleteModal
				title={t('sureDelete')}
				visible={isConfirmModalVisible}
				handleClickCancel={() => {
					setIsConfirmModalVisible(false)
				}}
				handleClickNo={() => {
					setIsConfirmModalVisible(false)
				}}
				handleClickYes={() => {
					cancelSale(selectedRowData)
				}}
			/>
			<IgnoreSaleModal
				visible={isCancelModalVisible}
				data={selectedRowData}
				onClose={() => {
					setIsCancelModalVisible(false)
					refetch()
				}}
			/>

			<SuccessModal
				getRef={(r) => {
					successRef.current = r
				}}
			/>
			<ErrorModal
				getRef={(r) => {
					errorRef.current = r
				}}
			/>
		</Layout>
	)
}

export default SalesOrderRequestAdmin
